/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService } from '@dm/core';
import { type Observable } from 'rxjs';

import { FileSystemModeE, MO } from '../../../../../common/constants';
import { type FilesystemSnapshot } from '../../../../../common/types/mo';
import { FilesystemSnapshotStrService, SnapTypeE } from './filesystem-snapshot-str.service';

@Injectable({ providedIn: 'root' })
export class FilesystemSnapshotService {
    constructor(
        public str: FilesystemSnapshotStrService,
        private http: HttpService,
    ) { }

    getFilesystemBatchRest(isCdp?: boolean): Rest {
        let rest: Rest;
        if (isCdp) {
            rest = this.http.makeUrl('fileSystem.getAssociate', undefined, true);
            rest.setParams({
                TYPE: MO.FILE_SYSTEM,
                ASSOCIATEOBJTYPE: MO.FILE_CDP,
            });
        } else {
            rest = this.http.makeUrl('fileSystem.getBatch', undefined, true);
            rest.setFilters([{
                field: 'SUBTYPE', matchMode: '!!', value: FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM,
            }]);
        }

        return rest;
    }

    getFilesystemSnapshotBatchRest(fsId: string, vstoreId: string, isCdp?: boolean, onlyUserSnap?: boolean): Rest {
        const rest = this.http.makeUrl('fsSnapshot.getBatch').setParams({ vstoreId });
        rest.setFilters(
            [{
                field: 'PARENTID',
                value: fsId,
                matchMode: '::',
            }],
        );
        if (isCdp) {
            // CDP场景
            rest.setParams({ snapType: SnapTypeE.HyperCDP });
        } else if (onlyUserSnap) {
            // 极简模式场景，支持查用户快照
            rest.setParams({ snapType: SnapTypeE.UserSnap });
        }
        return rest;
    }

    getFilesystemSnapshotcCountRest(fsId): Rest {
        return this.http.makeUrl('fsSnapshot.count').setFilters(
            [{
                field: 'PARENTID',
                value: fsId,
                matchMode: '::',
            }],
        );
    }

    /**
     * 获取文件系统快照数量
     */
    getFilesystemSnapshotcCount(fsId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fsSnapshot.count').setFilters(
            [{
                field: 'PARENTID',
                value: fsId,
                matchMode: '::',
            }],
        );

        return this.http.get(rest);
    }

    get(data, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fsSnapshot.get')
            .setParams(
                {
                    NAME: data.NAME,
                    dtreeId: data.dtreeId,
                    PARENTID: data.PARENTID,
                    PARENTNAME: data.PARENTNAME,
                    vstoreId: data.vstoreId,
                },
            );

        return this.http.get(rest, options);
    }

    getFsSshot(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fsSnapshot.get')
            .setParams(
                { ID: id },
            );
        return this.http.get(rest, options);
    }

    create(params: any, options?: BaseRequestOptionsArgs, isCdp?: boolean): Observable<HttpResponse> {
        const rest = this.http.makeUrl(isCdp ? 'fsCdp.post' : 'fsSnapshot.post');
        return this.http.post(rest, params, options);
    }

    modify(params: any, options?: BaseRequestOptionsArgs, isCdp?: boolean): Observable<HttpResponse> {
        const rest = this.http.makeUrl(isCdp ? 'fsCdp.put' : 'fsSnapshot.put');
        return this.http.put(rest, params, options);
    }

    delete(params, options?: BaseRequestOptionsArgs, isCdp?: boolean): Observable<HttpResponse> {
        const rest = this.http.makeUrl(isCdp ? 'fsCdp.delete' : 'fsSnapshot.delete').setParams(
            {
                NAME: params.NAME,
                PARENTID: params.PARENTID,
            },
        );
        if (params.vstoreId) {
            rest.setParams({ vstoreId: params.vstoreId });
        }
        return this.http.delete(rest, options);
    }

    rollback(data: FilesystemSnapshot, options?: BaseRequestOptionsArgs, isCdp?: boolean): Observable<HttpResponse> {
        const rest = this.http.makeUrl(isCdp ? 'fsCdp.rollback' : 'fsSnapshot.rollback');
        return this.http.put(rest, data, { ...options, mask: true });
    }

    modifyRollbackSpeed(data: FilesystemSnapshot, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fsSnapshot.modifyRollbackSpeed');
        return this.http.put(rest, data, { ...options, mask: true });
    }
}
